import React from "react";
import { X, CreditCard, Loader2, ShieldCheck } from "lucide-react";
import { useApp } from "../context/AppContext";

export default function PaymentModal() {
  const { payItem, payProcessing, closePayModal, startPayment, artist, membershipPrice } = useApp();
  if (!payItem) return null;

  const type = payItem.type;
  const isSong = type === "song";
  const isTicket = type === "ticket";
  const isRegistration = type === "registration";
  const isFanItem = !isRegistration;

  let title, amount, sub, cover;
  if (isSong) {
    title = payItem.song.title; amount = payItem.song.price; sub = "Single — one-time purchase"; cover = payItem.song.cover;
  } else if (isTicket) {
    title = payItem.event.title; amount = payItem.event.ticketPrice; sub = "Event ticket — QR delivered"; cover = artist.heroImage;
  } else if (isRegistration) {
    title = "Artist Portal Registration"; amount = payItem.price || 11.0; sub = "Platform fee — billed monthly"; cover = "/brand/s71-logo-sm.png";
  } else {
    title = artist.membership.title; amount = membershipPrice; sub = "Membership — 30 days of access"; cover = artist.heroImage;
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="payment-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => !payProcessing && closePayModal()} />
      <div className="relative z-10 w-full max-w-[420px] rounded-t-3xl sm:rounded-3xl bg-[#101012] border border-white/10 overflow-hidden">
        <div className="flex items-center justify-between px-5 h-14 border-b border-white/10">
          <h3 className="text-[13px] font-semibold tracking-[0.16em] uppercase">{isRegistration ? "Activate Portal" : "Checkout"}</h3>
          <button onClick={() => !payProcessing && closePayModal()} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10">
            <X size={16} />
          </button>
        </div>

        <div className="p-5">
          <div className="flex items-center gap-3 mb-5">
            <img src={cover} alt={title} className={`w-14 h-14 rounded-xl object-cover ${isRegistration ? "bg-white/5 p-2 object-contain" : ""}`} />
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-semibold text-white truncate">{title}</p>
              <p className="text-[12px] text-white/45">{sub}</p>
            </div>
            <div className="text-right">
              <p className="font-display text-[22px] text-white">${Number(amount).toFixed(2)}</p>
              <p className="text-[10px] text-white/40 uppercase">USD{isRegistration ? "/mo" : type === "membership" ? "/30 days" : ""}</p>
            </div>
          </div>

          <p className="text-[11px] uppercase tracking-wide text-white/40 mb-2">Pay with</p>
          <div className="space-y-3">
            {/* Stripe card is only for the platform registration fee (fan money must route to the artist via PayPal). */}
            {isRegistration && (
              <button
                data-testid="pay-stripe-btn"
                onClick={() => startPayment("stripe")}
                disabled={payProcessing}
                className="w-full h-12 rounded-xl bg-white text-black text-[14px] font-semibold flex items-center justify-center gap-2 hover:bg-white/85 disabled:opacity-60"
              >
                {payProcessing ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={18} />}
                Card (Stripe)
              </button>
            )}
            <button
              data-testid="pay-paypal-btn"
              onClick={() => startPayment("paypal")}
              disabled={payProcessing}
              className="w-full h-12 rounded-xl bg-[#ffc439] text-[#003087] text-[15px] font-bold flex items-center justify-center gap-1 hover:brightness-95 disabled:opacity-60"
            >
              {payProcessing ? <Loader2 size={16} className="animate-spin text-[#003087]" /> : (
                <><span className="italic">Pay</span><span className="italic text-[#0070ba]">Pal</span></>
              )}
            </button>
            {isFanItem && (
              <p className="flex items-center justify-center gap-1.5 text-[11px] text-white/45">
                <CreditCard size={13} /> No PayPal account needed — pay with any credit or debit card
              </p>
            )}
          </div>

          <div className="flex items-center justify-center gap-1.5 mt-5 text-[11px] text-white/35">
            <ShieldCheck size={13} /> {isRegistration ? "Secure checkout · platform registration fee" : "Secure checkout · 100% to the artist"}
          </div>
        </div>
      </div>
    </div>
  );
}
